import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Box, Checkbox, FormControlLabel, Typography } from "@mui/material";
import { format } from "date-fns";
import Page from "./Page";
import EntityList from "./EntityList";
import Dialog_Flex from "../ui/Dialog_Flex";
import Dialog_YesNo from "../ui/Dialog_YesNo";
import Input from "../elements/Input";
import Select from "../elements/Select";
import AutoComplete from "../elements/AutoComplete";
import useData from "../../hooks/useData";
import usePage from "../../hooks/usePage";

const OrdersTransmissionRoutesPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { executeAction } = useData();
  const { showMessage } = usePage();

  const clientId = location?.state?.clientId || null;
  const clientName = location?.state?.clientName || "";
  const returnPath = location?.state?.returnPath || "/clientorders";
  const returnState = location?.state?.returnState || {};

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [routes, setRoutes] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [retailers, setRetailers] = useState([]);
  const [banners, setBanners] = useState([]);
  const [editingRecord, setEditingRecord] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [openDeleteConfirm, setOpenDeleteConfirm] = useState(false);
  const [formData, setFormData] = useState({
    scopeType: "client",
    scopeId: "",
    profileId: "",
    priority: 0,
    isActive: true,
    effectiveFrom: "",
    effectiveTo: "",
  });

  useEffect(() => {
    if (!clientId) {
      setRoutes([]);
      setProfiles([]);
      setRetailers([]);
      setBanners([]);
      return;
    }

    loadRoutesData();
  }, [clientId]);

  const profileMap = useMemo(() => {
    return new Map(profiles.map((item) => [item.id, item]));
  }, [profiles]);

  const retailerMap = useMemo(() => {
    return new Map(retailers.map((item) => [item.id, item]));
  }, [retailers]);

  const bannerMap = useMemo(() => {
    return new Map(banners.map((item) => [item.id, item]));
  }, [banners]);

  const sortedRoutes = useMemo(() => {
    const scopeRank = {
      projectLocation: 0,
      banner: 1,
      retailer: 2,
      client: 3,
    };

    return [...routes].sort((left, right) => {
      const leftRank = scopeRank[left?.scopeType] ?? 9;
      const rightRank = scopeRank[right?.scopeType] ?? 9;
      if (leftRank !== rightRank) {
        return leftRank - rightRank;
      }

      const leftPriority = Number(left?.priority || 0);
      const rightPriority = Number(right?.priority || 0);
      if (leftPriority !== rightPriority) {
        return rightPriority - leftPriority;
      }

      return String(left?.id || "").localeCompare(String(right?.id || ""));
    });
  }, [routes]);

  const profileItems = useMemo(() => {
    return profiles.map((item) => ({
      id: item.id,
      name: item.name || item.id,
    }));
  }, [profiles]);

  const scopeTypeItems = [
    { id: "client", name: "Client" },
    { id: "retailer", name: "Retailer" },
    { id: "banner", name: "Banner" },
    { id: "projectLocation", name: "Project Location" },
  ];

  const scopeTargetItems = useMemo(() => {
    if (formData.scopeType === "retailer") {
      return retailers;
    }

    if (formData.scopeType === "banner") {
      return banners;
    }

    return [];
  }, [formData.scopeType, retailers, banners]);

  const scopeTargetLabel = useMemo(() => {
    if (formData.scopeType === "retailer") {
      return "Retailer";
    }
    if (formData.scopeType === "banner") {
      return "Banner";
    }
    if (formData.scopeType === "projectLocation") {
      return "Project Location ID";
    }
    return "Scope Target";
  }, [formData.scopeType]);

  const toDateInputValue = (value) => {
    if (!value) return "";
    const dateValue = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(dateValue.getTime())) return "";
    return format(dateValue, "yyyy-MM-dd");
  };

  const loadRoutesData = async () => {
    if (!clientId) return;

    setLoading(true);
    try {
      const [routeResults, profileResults, retailerResults, bannerResults] =
        await Promise.all([
          executeAction({
            entityName: "ClientTransmissionRoute",
            action: "get",
            filter: { clientId },
          }),
          executeAction({
            entityName: "ClientTransmissionProfile",
            action: "get",
            filter: { clientId },
          }),
          executeAction({
            entityName: "Retailer",
            action: "get",
            filter: {},
          }),
          executeAction({
            entityName: "Banner",
            action: "get",
            filter: {},
          }),
        ]);

      if (routeResults?.isSuccessful && Array.isArray(routeResults.data)) {
        setRoutes(routeResults.data);
      } else {
        setRoutes([]);
      }

      if (profileResults?.isSuccessful && Array.isArray(profileResults.data)) {
        setProfiles(profileResults.data);
      } else {
        setProfiles([]);
      }

      if (
        retailerResults?.isSuccessful &&
        Array.isArray(retailerResults.data)
      ) {
        setRetailers(
          retailerResults.data.map((item) => ({
            key: item.id,
            id: item.id,
            name: item.name || item.referenceId || item.id,
          })),
        );
      } else {
        setRetailers([]);
      }

      if (bannerResults?.isSuccessful && Array.isArray(bannerResults.data)) {
        setBanners(
          bannerResults.data.map((item) => ({
            key: item.id,
            id: item.id,
            name: item.name || item.referenceId || item.id,
          })),
        );
      } else {
        setBanners([]);
      }
    } catch (err) {
      setRoutes([]);
      setProfiles([]);
      setRetailers([]);
      setBanners([]);
      showMessage(
        err?.message || "Failed to load transmission routes",
        "error",
      );
    } finally {
      setLoading(false);
    }
  };

  const openAddDialog = () => {
    setEditingRecord(null);
    setFormData({
      scopeType: "client",
      scopeId: "",
      profileId: "",
      priority: 0,
      isActive: true,
      effectiveFrom: "",
      effectiveTo: "",
    });
    setOpenDialog(true);
  };

  const openEditDialog = (record) => {
    setEditingRecord(record);
    setFormData({
      scopeType: record?.scopeType || "client",
      scopeId: record?.scopeId || "",
      profileId: record?.profileId || "",
      priority: Number(record?.priority || 0),
      isActive: record?.isActive !== false,
      effectiveFrom: toDateInputValue(record?.effectiveFrom),
      effectiveTo: toDateInputValue(record?.effectiveTo),
    });
    setOpenDialog(true);
  };

  const updateField = (field, value) => {
    setFormData((previous) => ({ ...previous, [field]: value }));
  };

  const updateScopeType = (value) => {
    setFormData((previous) => ({
      ...previous,
      scopeType: value,
      scopeId: "",
    }));
  };

  const saveRoute = async () => {
    if (!clientId) {
      showMessage("Missing client context", "error");
      return;
    }

    if (profiles.length === 0) {
      showMessage("Create at least one transmission profile first", "error");
      return;
    }

    if (!String(formData.profileId || "").trim()) {
      showMessage("Transmission profile is required", "error");
      return;
    }

    const normalizedScopeId = String(formData.scopeId || "").trim();
    if (formData.scopeType !== "client" && !normalizedScopeId) {
      showMessage("Scope target is required for this scope type", "error");
      return;
    }

    if (
      formData.effectiveFrom &&
      formData.effectiveTo &&
      new Date(formData.effectiveFrom) > new Date(formData.effectiveTo)
    ) {
      showMessage("Effective From cannot be after Effective To", "error");
      return;
    }

    const data = {
      clientId,
      scopeType: formData.scopeType,
      scopeId: formData.scopeType === "client" ? null : normalizedScopeId,
      profileId: String(formData.profileId).trim(),
      isActive: !!formData.isActive,
      priority: Number(formData.priority || 0),
      effectiveFrom: formData.effectiveFrom || null,
      effectiveTo: formData.effectiveTo || null,
    };

    setSaving(true);
    try {
      const results = await executeAction({
        entityName: "ClientTransmissionRoute",
        action: editingRecord?.id ? "update" : "add",
        data: editingRecord?.id ? { ...data, id: editingRecord.id } : data,
      });

      if (!results?.isSuccessful) {
        showMessage(results?.message || "Failed to save route", "error");
        return;
      }

      showMessage("Transmission route saved", "success");
      setOpenDialog(false);
      await loadRoutesData();
    } catch (err) {
      showMessage(err?.message || "Failed to save route", "error");
    } finally {
      setSaving(false);
    }
  };

  const deleteRoute = async () => {
    if (!editingRecord?.id) return;

    setSaving(true);
    try {
      const results = await executeAction({
        entityName: "ClientTransmissionRoute",
        action: "delete",
        data: { id: editingRecord.id },
      });

      if (!results?.isSuccessful) {
        showMessage(results?.message || "Failed to delete route", "error");
        return;
      }

      showMessage("Transmission route deleted", "success");
      setOpenDeleteConfirm(false);
      setOpenDialog(false);
      setEditingRecord(null);
      await loadRoutesData();
    } catch (err) {
      showMessage(err?.message || "Failed to delete route", "error");
    } finally {
      setSaving(false);
    }
  };

  const resolveScopeTargetName = (row) => {
    if (row?.scopeType === "client") {
      return "Current client";
    }

    if (row?.scopeType === "retailer") {
      return retailerMap.get(row.scopeId)?.name || row?.scopeId || "";
    }

    if (row?.scopeType === "banner") {
      return bannerMap.get(row.scopeId)?.name || row?.scopeId || "";
    }

    if (row?.scopeType === "projectLocation") {
      return row?.scopeId || "";
    }

    return row?.scopeId || "";
  };

  const resolveProfileName = (row) => {
    const profile = profileMap.get(row?.profileId);
    return profile?.name || row?.profileId || "";
  };

  const formatDateValue = (value) => {
    if (!value) {
      return "";
    }
    const dateValue = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(dateValue.getTime())) {
      return "";
    }
    return format(dateValue, "yyyy-MM-dd");
  };

  const tableColumns = [
    {
      name: "scopeType",
      label: "Scope",
      clickable: true,
      width: "140px",
    },
    {
      name: "scopeId",
      label: "Scope Target",
      width: "240px",
      render: (row) => resolveScopeTargetName(row),
    },
    {
      name: "profileId",
      label: "Profile",
      width: "240px",
      render: (row) => resolveProfileName(row),
    },
    {
      name: "priority",
      label: "Priority",
      width: "90px",
    },
    {
      name: "effectiveFrom",
      label: "Effective From",
      width: "130px",
      render: (row) => formatDateValue(row?.effectiveFrom),
    },
    {
      name: "effectiveTo",
      label: "Effective To",
      width: "130px",
      render: (row) => formatDateValue(row?.effectiveTo),
    },
    {
      name: "isActive",
      label: "Active",
      width: "90px",
      render: (row) => (row?.isActive ? "Yes" : "No"),
    },
  ];

  const handleToolbarClick = (name) => {
    if (name === "back") {
      navigate(returnPath, { state: returnState });
      return;
    }

    if (name === "add") {
      openAddDialog();
      return;
    }

    if (name === "refresh") {
      loadRoutesData();
    }
  };

  const handleRowClick = (_column, id) => {
    const selected = routes.find((row) => row.id === id);
    if (!selected) return;
    openEditDialog(selected);
  };

  return (
    <>
      <Page
        pageName="Transmission Routes"
        entityName={clientName || ""}
        includeToolbar
        onToolItemClick={handleToolbarClick}
        items={[
          { name: "back", label: "Back", iconName: "ArrowBack" },
          { name: "add", label: "Add", iconName: "AddCircleOutline" },
          { name: "refresh", label: "Refresh", iconName: "Refresh" },
        ]}
      >
        {!clientId ? (
          <Typography>Client context is missing for this page.</Typography>
        ) : (
          <Box>
            <Typography sx={{ mb: 1 }}>
              Route each order scope to a transmission profile. Higher priority
              wins inside the same scope.
            </Typography>

            {profiles.length === 0 && (
              <Typography sx={{ mb: 1, color: "warning.main" }}>
                No transmission profiles found for this client. Create a profile
                before adding routes.
              </Typography>
            )}

            {loading ? (
              <Typography>Loading transmission routes...</Typography>
            ) : (
              <EntityList
                entityList={sortedRoutes}
                columns={tableColumns}
                onRowClick={handleRowClick}
                tableHeight="calc(100vh - 250px)"
                allowSort={false}
              />
            )}
          </Box>
        )}
      </Page>

      <Dialog_Flex
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        title={editingRecord?.id ? "Edit Route" : "Add Route"}
        message=""
        actions={[
          ...(editingRecord?.id
            ? [{ name: "delete", label: saving ? "deleting" : "delete" }]
            : []),
          { name: "cancel", label: "cancel" },
          { name: "save", label: saving ? "saving" : "save" },
        ]}
        onAction={(action) => {
          if (action === "delete" && editingRecord?.id && !saving) {
            setOpenDeleteConfirm(true);
            return;
          }
          if (action === "cancel") {
            setOpenDialog(false);
            return;
          }
          if (action === "save" && !saving) {
            saveRoute();
          }
        }}
      >
        <Box
          sx={{
            px: 3,
            pb: 2,
            pt: 1,
            minWidth: 520,
            display: "flex",
            flexDirection: "column",
            gap: 1,
          }}
        >
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1 }}>
            <Select
              label="Scope"
              value={formData.scopeType}
              items={scopeTypeItems}
              onChange={(value) => updateScopeType(value)}
            />

            {formData.scopeType === "client" ? (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                >
                  Scope Target
                </Typography>
                <Typography>Auto: current client</Typography>
              </Box>
            ) : formData.scopeType === "projectLocation" ? (
              <Input
                label={scopeTargetLabel}
                value={formData.scopeId}
                onChange={(value) => updateField("scopeId", value)}
              />
            ) : (
              <AutoComplete
                id="routeScopeTarget"
                label={scopeTargetLabel}
                value={formData.scopeId || ""}
                onChange={(value) => updateField("scopeId", value || "")}
                items={scopeTargetItems}
                width="100%"
              />
            )}

            <Select
              label="Transmission Profile"
              value={formData.profileId}
              items={profileItems}
              onChange={(value) => updateField("profileId", value)}
            />

            <Input
              label="Priority"
              type="number"
              value={formData.priority}
              onChange={(value) => updateField("priority", value)}
            />

            <Input
              label="Effective From"
              type="date"
              value={formData.effectiveFrom}
              onChange={(value) => updateField("effectiveFrom", value)}
            />

            <Input
              label="Effective To"
              type="date"
              value={formData.effectiveTo}
              onChange={(value) => updateField("effectiveTo", value)}
            />
          </Box>

          <FormControlLabel
            control={
              <Checkbox
                checked={!!formData.isActive}
                onChange={(_event, checked) => updateField("isActive", checked)}
              />
            }
            label="Active"
          />
        </Box>
      </Dialog_Flex>

      <Dialog_YesNo
        open={openDeleteConfirm}
        onClose={() => setOpenDeleteConfirm(false)}
        title="Delete Route"
        message="Delete this transmission route? This cannot be undone."
        onYes={() => deleteRoute()}
        onNo={() => setOpenDeleteConfirm(false)}
      />
    </>
  );
};

export default OrdersTransmissionRoutesPage;
